<template>
  <div class="min-h-[100dvh] overflow-y-auto flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4 py-8">
    <!-- Logo de la empresa -->
    <div class="mb-4 sm:mb-6 text-center flex flex-col items-center mt-auto pt-8">
      <div class="flex items-center justify-center">
        <!-- Logo de Jean Informatica -->
        <img 
          src="@/assets/logo/logo-jean.jpg" 
          alt="Jean Informatica Logo" 
          class="w-64 sm:w-72 md:w-80 h-auto max-h-[12vh] object-contain cursor-pointer"
          @error="handleImageError"
          @click="$router.push('/')"
        />
      </div>
    </div>

    <!-- Formulario de Recuperación -->
    <div class="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden mb-auto">
      <div class="p-5 sm:p-8">
        <div class="mb-6 text-center">
          <h2 class="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white">
            ¿Olvidaste tu contraseña?
          </h2>
          <p class="text-sm text-gray-600 dark:text-gray-300 mt-2">
            Ingresa tu correo electrónico y te enviaremos las instrucciones para recuperarla.
          </p>
        </div>

        <form @submit.prevent="handleRecovery">
          <!-- Email -->
          <div class="mb-6">
            <label for="email" class="block mb-1 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
              Correo Electrónico
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </div>
              <input
                id="email"
                v-model="email"
                type="email"
                required
                class="pl-10 w-full px-3 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors text-sm"
                placeholder="Ingresa tu correo electrónico"
              />
            </div>
          </div>

          <!-- Botón de Recuperación -->
          <button
            type="submit"
            :disabled="loading"
            class="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2 sm:py-2.5 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm mb-4"
          >
            <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span v-if="!loading">Enviar enlace de recuperación</span>
            <span v-else>Procesando...</span>
          </button>
        </form>
      </div>

      <!-- Pie del formulario -->
      <div class="px-5 py-3 sm:px-8 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 text-center">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          ¿Recordaste tu contraseña?
          <button
            type="button"
            @click="$router.push('/login')"
            class="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            Inicia sesión aquí
          </button>
        </p>
      </div>
    </div>

    <!-- Información adicional (Branding Profesional) -->
    <div class="mt-6 text-center mb-8">
      <div class="flex flex-col items-center gap-2">
        <div class="flex items-center gap-2 text-[10px] text-slate-400 dark:text-gray-500 uppercase tracking-[0.2em] font-bold">
          <span>Plataforma de Gestión</span>
          <span class="w-1 h-1 rounded-full bg-slate-300 dark:bg-gray-700"></span>
          <span>v1.0.0</span>
        </div>
        <div class="flex items-center justify-center gap-1.5 text-xs text-slate-500 dark:text-gray-400">
          <span class="opacity-80">Desarrollado con</span>
          <svg class="w-3.5 h-3.5 text-rose-500 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
          <span class="opacity-80">por</span>
          <span class="font-black text-indigo-600 dark:text-indigo-400 tracking-tight">Jean Informático C.A.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';

export default {
  name: 'ForgotPasswordPage',
  data() {
    return {
      email: '',
      loading: false,
      logoError: false,
    };
  },
  mounted() {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  },
  methods: {
    handleImageError() {
      this.logoError = true;
    },
    async handleRecovery() {
      if (!this.email) return;
      
      this.loading = true;
      
      try {
        // Simulamos un retraso de red
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // NOTA: Conectar con API real de recuperación
        Swal.fire({
          icon: 'success',
          title: '¡Enlace Enviado!',
          text: `Si el correo ${this.email} está registrado, recibirás un enlace para restablecer tu contraseña.`,
          confirmButtonColor: '#3B82F6'
        }).then(() => {
          this.$router.push('/login');
        });

      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Hubo un problema al procesar la solicitud.',
          confirmButtonColor: '#3B82F6'
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.min-h-\\[100dvh\\] {
  animation: fadeIn 0.5s ease-out;
}

/* Transiciones suaves */
.transition-colors {
  transition-property: background-color, border-color, color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Gradiente animado para el botón */
button.bg-gradient-to-r {
  background-size: 200% 200%;
  animation: gradientShift 3s ease infinite;
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* Efecto hover para tarjetas */
.bg-white, .dark\\:bg-gray-800 {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.bg-white:hover, .dark\\:bg-gray-800:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.dark .bg-white:hover, .dark .dark\\:bg-gray-800:hover {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}
</style>
